import React, { useEffect, useRef, useState } from "react";
import { Skeleton } from "../Skeleton/Skeleton";
import { buildSrcSet } from "../../../utils/imageVariants";
import "./ProgressiveImage.css";

/**
 * Drop-in replacement for a plain <img> that reserves its space up front,
 * shows the existing warm-toned Skeleton while the image downloads, and only
 * reveals the image once it has fully decoded (avoids partial-paint flashes
 * and skips the skeleton entirely for already-cached images).
 *
 * Two layout modes:
 *  - fill (default false): owns its own sized, relatively-positioned box —
 *    use for images with no existing wrapper (Hero, TatoClub, About photos).
 *  - fill=true: absolutely fills the nearest positioned ancestor — use inside
 *    an existing sized wrapper like .sp-card-media.
 */
const ProgressiveImage = ({
  src,
  alt = "",
  fallbackSrc,
  loading = "lazy",
  fetchPriority,
  width = "100%",
  height = "100%",
  radius = 0,
  fit = "cover",
  fill = false,
  className = "",
  imgClassName = "",
  style,
  onLoad,
  // Responsive variants — see server/models/company.js's logoImageWidths
  // and client/src/utils/imageVariants.js's buildSrcSet for the naming
  // convention. Both default to unused so every existing call site (no
  // imageWidths passed) renders exactly as before, with no srcSet/sizes
  // attributes at all.
  imageWidths,
  sizes,
}) => {
  const imgRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | loaded | error
  const [resolvedSrc, setResolvedSrc] = useState(src);

  useEffect(() => {
    setResolvedSrc(src);
    setStatus("loading");
  }, [src]);

  useEffect(() => {
    const node = imgRef.current;
    // Already in the browser cache by the time we attach — skip the skeleton,
    // no flash.
    if (node && node.complete && node.naturalWidth > 0) {
      setStatus("loaded");
    }
  }, [resolvedSrc]);

  useEffect(() => {
    const node = imgRef.current;
    if (node && fetchPriority) {
      node.setAttribute("fetchpriority", fetchPriority);
    }
  }, [fetchPriority, resolvedSrc]);

  const handleLoad = (e) => {
    const node = e.target;
    if (node.decode) {
      node
        .decode()
        .then(() => setStatus("loaded"))
        .catch(() => setStatus("loaded"));
    } else {
      setStatus("loaded");
    }
    onLoad?.(e);
  };

  const handleError = () => {
    if (fallbackSrc && resolvedSrc !== fallbackSrc) {
      setResolvedSrc(fallbackSrc);
      return;
    }
    setStatus("error");
  };

  // imageWidths describes variants of `src` specifically — once handleError
  // has swapped resolvedSrc over to fallbackSrc, those widths no longer
  // apply to what's being displayed, so srcSet is dropped rather than
  // pointing at (nonexistent) variants of the placeholder image.
  const srcSet = resolvedSrc === src ? buildSrcSet(resolvedSrc, imageWidths) : undefined;

  const wrapperStyle = fill
    ? { position: "absolute", inset: 0, borderRadius: radius, overflow: "hidden", ...style }
    : { position: "relative", width, height, borderRadius: radius, overflow: "hidden", ...style };

  return (
    <div className={`sp-progressive-image ${className}`} style={wrapperStyle}>
      {status === "loading" && (
        <Skeleton width="100%" height="100%" radius={0} style={{ position: "absolute", inset: 0 }} />
      )}
      {status === "error" ? (
        <div className="sp-progressive-image-fallback" aria-label={alt} role={alt ? "img" : undefined} />
      ) : (
        <img
          ref={imgRef}
          src={resolvedSrc}
          srcSet={srcSet}
          sizes={srcSet ? sizes : undefined}
          alt={alt}
          loading={loading}
          onLoad={handleLoad}
          onError={handleError}
          className={`sp-progressive-image-img ${imgClassName} ${status === "loaded" ? "is-loaded" : ""}`}
          style={{ width: "100%", height: "100%", objectFit: fit, display: "block" }}
        />
      )}
    </div>
  );
};

export default ProgressiveImage;
